import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import { addToCart } from "../../redux/actions/addToCartAction";
import "react-phone-input-2/lib/style.css";
import { randomColors } from "../../data/colors";

const Cart = ({ addToCart, carts }) => {
  const navigate = useNavigate();
  const [quantities, setQuantities] = useState({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    organization: "",
    designation: "",
    showDate: "",
    email: "",
    phone: "",
    address: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isProgressHighlighted, setIsProgressHighlighted] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const initialQuantities = {};
    carts.forEach((item) => {
      initialQuantities[item.id] = item.quantity || 1;
    });
    setQuantities(initialQuantities);
  }, [carts]);

  const handleQuantityChange = (id, newQuantity) => {
    setQuantities({
      ...quantities,
      [id]: Math.max(1, newQuantity),
    });
  };
  const validateField = (name, value) => {
    switch (name) {
      case "firstName":
        return value.trim() ? "" : "First name is required";
      case "lastName":
        return value.trim() ? "" : "Last name is required";
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) return "Email is required";
        return emailRegex.test(value)
          ? ""
          : "Please enter a valid email address";
      case "phone":
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        if (!value.trim()) return "Phone number is required";
        return phoneRegex.test(value)
          ? ""
          : "Please enter a valid phone number";
      case "showDate":
        if (!value) return "Date of show is required";
        const selectedDate = new Date(value);
        const today = new Date();
        return selectedDate >= today ? "" : "Please select a future date";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate on change if field has been touched
    if (touched[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  const [message, setMessage] = useState("");
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true); // Start loading

    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    setTouched(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    if (Object.keys(newErrors).length === 0) {
      const data = {
        ...formData, // Include form data in the payload
        carts, // Include the carts array
      };

      axios
        .post(`${process.env.REACT_APP_API_URL}order`, data)
        .then((response) => {
          setMessage("");
          setErrors({});
          window.scrollTo({ top: 0, behavior: "smooth" });
          setShowSuccessModal(true);
          setIsProgressHighlighted(true);
          console.log("Form submitted:", formData);
          console.log("Response data:", response.data);
          addToCart([]);
          setIsLoading(false); // Stop loading after success
        })
        .catch((error) => {
          console.error("Error adding to order:", error); // Handle errors if any
          const apiErrors = error.response?.data?.error;
          setMessage(error.response?.data?.message);
          if (apiErrors) {
            setErrors((prevErrors) => ({
              ...prevErrors,
              ...apiErrors, // Assuming error response structure like { carts: [...], ... }
            }));
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
          setIsLoading(false); // Stop loading after error
        });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setIsLoading(false); // Stop loading if validation errors exist
    }
  };

  const handleRemoveItem = (id) => {
    const updatedCart = carts.filter((item) => item.id !== id);
    addToCart(updatedCart); // Update the cart state
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-4">
        {/* Progress Bar */}
        <div className="flex flex-row items-center mb-8 justify-center gap-4 sm:gap-0">
          <div className="flex items-center">
            <div className="bg-purple-700 rounded-full h-6 w-6 sm:h-8 sm:w-8 flex items-center justify-center text-white text-sm md:text-base">
              1
            </div>
            <span className="ml-2 font-medium text-xs md:text-base">
              SHOPPING CART
            </span>
          </div>
          <div className="hidden sm:block h-px bg-gray-300 w-8 mx-4" />
          <div className="flex items-center">
            <div
              className={`${
                isProgressHighlighted
                  ? "bg-purple-700 text-white"
                  : "bg-gray-300 text-gray-400"
              } rounded-full h-6 w-6 sm:h-8 sm:w-8 flex items-center justify-center text-sm md:text-base`}
            >
              2
            </div>
            <span className="ml-2 text-xs md:text-base">ORDER COMPLETE</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="lg:col-span-1">
            <div className="grid grid-cols-[1.5fr_1fr] sm:grid-cols-[2fr_1fr] gap-1 mb-4 text-gray-700 bg-white/50 p-3 rounded-lg">
              <div className="font-medium">PRODUCT</div>
              <div className="font-medium text-center sm:text-left">
                QUANTITY
              </div>
            </div>

            {carts.map((item) => (
              <div key={item.id} className="border-t border-purple-100 py-4">
                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4 items-start">
                  {/* Product Info Section */}
                  <div className="flex flex-col md:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4">
                    <button
                      className="text-gray-400 hover:text-red-500 transition-colors text-xl p-1"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      ×
                    </button>

                    {/* Product Image */}
                    <div className="bg-white px-2 rounded-lg border border-purple-100 w-full md:w-24">
                      <img
                        src={item.image}
                        alt="Product Image"
                        className="w-full sm:w-24 h-36 object-contain mx-auto"
                      />
                    </div>

                    {/* Product Details */}
                    <div className="space-y-3 w-full">
                      <h3 className="font-semibold text-gray-800">
                        {item.name || "Product Name"}
                      </h3>

                      {/* Custom Text */}
                      {item.text && (
                        <div className="bg-white/50 px-2 rounded-lg">
                          <span className="text-sm text-gray-600">
                            Custom Text:
                          </span>
                          <p className="text-gray-800 break-words">
                            {item.text}
                          </p>
                        </div>
                      )}
                      {item?.totalPrice && (
                        <div className="bg-white/50 px-2 rounded-lg">
                          <span className="text-sm text-gray-600">
                            Total Price:
                          </span>
                          <p className="text-gray-800 break-words">
                            {item?.totalPrice}
                          </p>
                        </div>
                      )}
                      {item?.selectedType && (
                        <div className="bg-white/50 px-2 rounded-lg">
                          <span className="text-sm text-gray-600">
                            Selected Type:
                          </span>
                          <p className="text-gray-800 break-words">
                            {item?.selectedType}
                          </p>
                        </div>
                      )}
                      {item?.selectedKit && (
                        <div className="bg-white/50 px-2 rounded-lg">
                          <span className="text-sm text-gray-600">
                            Selected Kit:
                          </span>
                          <p className="text-gray-800 break-words">
                            {item?.selectedKit}
                          </p>
                        </div>
                      )}

                      {/* Colors */}
                      <div className="flex flex-wrap gap-2">
                        {item.colors?.map((color, index) => (
                          <div key={index} className="flex items-center">
                            <div className="text-xs">
                              <span className="text-gray-600">
                                <div className="color-icons flex space-x-2">
                                  {randomColors.map(
                                    (colorIcon) =>
                                      colorIcon.name === color.name && (
                                        <img
                                          key={colorIcon.name}
                                          src={colorIcon.icon}
                                          alt={colorIcon.name}
                                          className="w-8 h-8 md:w-10 md:h-10"
                                        />
                                      )
                                  )}
                                </div>
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Sparkle Text */}
                      {(item?.sparkleText || item?.selectedSparkle) && (
                        <div className="bg-white/50 px-2 rounded-lg">
                          <p className="text-sm text-gray-600">
                            Sparkle:{" "}
                            {item?.selectedSparkle && (
                              <span className="text-sm text-gray-800">
                                {item.selectedSparkle}
                              </span>
                            )}
                          </p>
                          <>
                            {item?.sparkleText && (
                              <span className="text-gray-600 ml-2">
                                {item.sparkleText}
                              </span>
                            )}
                          </>
                        </div>
                      )}

                      {item?.alignText && (
                        <div className="bg-white/50 px-2 rounded-lg">
                          <p className="text-sm text-gray-600">
                            Text Postion:{" "}
                            <span className="text-sm text-gray-800">
                              {item.alignText}
                            </span>
                          </p>
                        </div>
                      )}
                      {/* Logo */}
                      {item.logo && (
                        <div className="bg-white/50 px-2 rounded-lg">
                          <span className="text-sm text-gray-600 block mb-1">
                            Logo:
                          </span>
                          <img
                            src={
                              typeof item.logo === "string"
                                ? item.logo
                                : URL.createObjectURL(item.logo)
                            }
                            alt="Custom Logo"
                            className="w-16 h-16 object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center justify-center md:justify-start space-x-2 mt-4 md:mt-0">
                    <button
                      className="border border-purple-200 px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors"
                      onClick={() =>
                        handleQuantityChange(item.id, quantities[item.id] - 1)
                      }
                    >
                      -
                    </button>
                    <input
                      type="text"
                      value={quantities[item.id] || 1}
                      className="border border-purple-200 w-14 text-center rounded-lg p-2"
                      readOnly
                    />
                    <button
                      className="border border-purple-200 px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors"
                      onClick={() =>
                        handleQuantityChange(item.id, quantities[item.id] + 1)
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/");
                }}
                className="border border-purple-700 text-purple-700 px-4 py-2 w-full sm:w-auto text-center"
              >
                ← CONTINUE SHOPPING
              </button>
              <button className="bg-purple-400 text-white px-4 py-2 w-full sm:w-auto text-center">
                UPDATE CART
              </button>
            </div>
          </div>

          {/* Cart Totals */}
          <div className="lg:col-span-1">
            <div className="border p-4 sm:p-6 rounded-lg">
              <h2 className="text-xl font-bold mb-2">Order Details</h2>
              <p className="text-gray-600 text-sm mb-8">
                Please fill out the form below with accurate details to ensure
                prompt confirmation.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {message && !errors.carts && (
                  <div className="bg-red-100 text-red-600 p-4 rounded-md mb-4">
                    <strong>{message}</strong>
                  </div>
                )}
                {errors.carts && (
                  <div className="bg-red-100 text-red-600 p-4 rounded-md mt-4">
                    <strong>{errors.carts}</strong>
                  </div>
                )}

                {/* First Name and Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter first name"
                      className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all ${
                        touched.firstName && errors.firstName
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {touched.firstName && errors.firstName && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter last name"
                      className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all ${
                        touched.lastName && errors.lastName
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {touched.lastName && errors.lastName && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Organization and Designation */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organization's Name
                    </label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder="Enter organization name (optional)"
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Designation
                    </label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      placeholder="Enter designation (optional)"
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Date of Show */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Show <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="showDate"
                    value={formData.showDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all ${
                      touched.showDate && errors.showDate
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {touched.showDate && errors.showDate && (
                    <p className="text-red-500 text-sm mt-2">
                      {errors.showDate}
                    </p>
                  )}
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter email address"
                    className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all ${
                      touched.email && errors.email
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {touched.email && errors.email && (
                    <p className="text-red-500 text-sm mt-2">{errors.email}</p>
                  )}
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <PhoneInput
                    id="phone"
                    name="phone"
                    country="us"
                    value={formData.phone}
                    onChange={(phone) =>
                      handleChange({ target: { name: "phone", value: phone } })
                    }
                    onBlur={handleBlur}
                    placeholder="Enter phone number"
                    autoFormat={true}
                    enableAreaCodes={true}
                    enableSearch={true}
                    containerStyle={{ width: "100%" }}
                    inputStyle={{
                      width: "100%",
                      display: "block",
                      height: "calc(1.5em + .75rem + 2px)",
                      fontSize: "1rem",
                      fontWeight: "400",
                      lineHeight: "1.5",
                      color: "#495057",
                      backgroundColor: "#fff",
                      backgroundClip: "padding-box",
                      border: "1px solid #eee",
                      borderRadius: ".25rem",
                      transition:
                        "border-color .15s ease-in-out, box-shadow .15s ease-in-out",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                    className={`focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all ${
                      touched.phone && errors.phone
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {touched.phone && errors.phone && (
                    <p className="text-red-500 text-sm mt-2">{errors.phone}</p>
                  )}
                </div>

                {/* Full Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter full address (optional)"
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className={`w-full bg-purple-700 text-white py-3 mt-8 rounded-md hover:bg-purple-800 transition-colors focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    isLoading ? "cursor-not-allowed opacity-75" : ""
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin h-5 w-5 mr-2 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        ></path>
                      </svg>
                      Processing...
                    </div>
                  ) : (
                    "PROCEED ORDER"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white p-4 sm:p-6 rounded-lg max-w-md w-full mx-4">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">
                Thank You for Placing Your Order!
              </h2>
              <p className="text-gray-600 mb-6">
                We appreciate your order. Our team will reach out to you via
                phone and email to confirm the order details and guide you
                through the invoicing process.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-purple-700 text-white py-2 rounded-md hover:bg-purple-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    carts: state.addToCartReducer, // Assuming carts are in this reducer
  };
};

const mapDispatchToProps = (dispatch) => ({
  addToCart: (totalBill) => dispatch(addToCart(totalBill)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
